/**
 * SNES Chat - Chat Screen
 *
 * Conversational chat interface over the Trackaa chat API (deterministic
 * fast path, else a Claude agent over the MCP tool registry).
 * User Story 2: Chat with MCP Server (Priority: P1)
 */

import type { ChatMessage } from './types';
import {
  getState,
  subscribe,
  addMessage,
  updateMessage,
  setInputValue,
  setLoading,
} from './state';
import { generateUUID, getTimestamp, escapeHtml, formatTimestamp } from './utils';

// ============================================================================
// Configuration
// ============================================================================

const API_ENDPOINT = 'https://api.trackaa.app';
const API_KEY = import.meta.env.VITE_API_KEY || '';

// ============================================================================
// State
// ============================================================================

let chatContainer: HTMLElement | null = null;
let messagesContainer: HTMLElement | null = null;
let inputElement: HTMLInputElement | null = null;
let sendButton: HTMLButtonElement | null = null;
let unsubscribe: (() => void) | null = null;

// ============================================================================
// API
// ============================================================================

interface ChatResponse {
  success: boolean;
  response?: string;
  error?: string;
}

/**
 * Send a message to the AI chat endpoint
 */
async function sendChatMessage(message: string): Promise<ChatResponse> {
  // Build conversation history from state (last 10 messages for context)
  const state = getState();
  // Exclude PENDING messages. A "Thinking..." placeholder is added to state
  // before this runs, so the history posted used to end with it. The server
  // hands follow-ups to a model that reads this transcript, and it strips a
  // trailing echo of the current question (added to state just above) and any
  // placeholder before doing so — sending them is redundant either way.
  const history = state.messages
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.status !== 'pending')
    .slice(-10)
    .map((m) => ({ role: m.role, content: m.content }));

  const response = await fetch(`${API_ENDPOINT}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
    },
    body: JSON.stringify({ message, history }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// Message Handling
// ============================================================================

/**
 * Add a system message to the chat
 */
function addSystemMessage(content: string): void {
  const message: ChatMessage = {
    id: generateUUID(),
    role: 'system',
    content,
    timestamp: getTimestamp(),
    status: 'sent',
  };
  addMessage(message);
}

/**
 * Handle sending a message
 */
async function handleSendMessage(): Promise<void> {
  const state = getState();
  const content = state.inputValue.trim();

  if (!content || state.isLoading) return;

  // Clear input immediately
  setInputValue('');
  if (inputElement) {
    inputElement.value = '';
  }

  // Add user message
  const userMessage: ChatMessage = {
    id: generateUUID(),
    role: 'user',
    content,
    timestamp: getTimestamp(),
    status: 'sent',
  };
  addMessage(userMessage);

  // Set loading state
  setLoading(true);
  updateSendButtonState(true);

  // Add assistant placeholder
  const assistantId = generateUUID();
  const assistantMessage: ChatMessage = {
    id: assistantId,
    role: 'assistant',
    content: 'Thinking...',
    timestamp: getTimestamp(),
    status: 'pending',
  };
  addMessage(assistantMessage);

  try {
    const result = await sendChatMessage(content);

    if (result.success && result.response) {
      updateMessage(assistantId, {
        content: result.response,
        status: 'sent',
      });
    } else {
      updateMessage(assistantId, {
        content: `Error: ${result.error || 'Unknown error occurred'}`,
        status: 'error',
      });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Request failed';
    updateMessage(assistantId, {
      content: `Error: ${errorMessage}`,
      status: 'error',
    });
  } finally {
    setLoading(false);
    updateSendButtonState(false);
  }
}

/**
 * Handle input keypress
 */
function handleInputKeypress(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleSendMessage();
  }
}

/**
 * Handle input change
 */
function handleInputChange(event: Event): void {
  const target = event.target as HTMLInputElement;
  setInputValue(target.value);
}

/**
 * Handle input focus - scroll chat to bottom on mobile keyboard open
 */
function handleInputFocus(): void {
  setTimeout(() => {
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
    inputElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, 300);
}

/**
 * Handle input blur - reset scroll on mobile keyboard close
 */
function handleInputBlur(): void {
  setTimeout(() => {
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, 100);
}

// ============================================================================
// Rendering
// ============================================================================

/**
 * Update send button state
 */
function updateSendButtonState(isLoading: boolean): void {
  if (!sendButton) return;

  sendButton.disabled = isLoading;
  sendButton.textContent = isLoading ? '...' : 'SEND';
  sendButton.classList.toggle('loading', isLoading);
}

/**
 * Render a single message
 */
function renderMessage(message: ChatMessage): string {
  const roleClass = `message-${message.role}`;
  const statusClass = message.status === 'error' ? 'message-error' : '';
  const time = formatTimestamp(message.timestamp);
  const content = escapeHtml(message.content).replace(/\n/g, '<br>');

  const roleIcon = {
    user: '>',
    assistant: '<',
    system: '*',
  }[message.role];

  return `
    <div class="chat-message ${roleClass} ${statusClass}">
      <span class="message-icon">${roleIcon}</span>
      <div class="message-content">
        <pre>${content}</pre>
        <span class="message-time">${time}</span>
      </div>
    </div>
  `;
}

/**
 * Render all messages
 */
function renderMessages(): void {
  if (!messagesContainer) return;

  const state = getState();
  const messagesHtml = state.messages.map(renderMessage).join('');

  messagesContainer.innerHTML =
    messagesHtml ||
    `
    <div class="chat-empty">
      <p>Welcome to Trackaa!</p>
      <p class="hint">Ask me anything about aircraft, flights, or maintenance.</p>
    </div>
  `;

  // Scroll to bottom
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

/**
 * Handle state changes
 */
function handleStateChange(): void {
  renderMessages();
}

/**
 * Render the chat screen
 */
export function renderChatScreen(container: HTMLElement): void {
  chatContainer = container;

  container.innerHTML = `
    <div class="screen chat-screen">
      <header class="chat-header">
        <h1>TRACKAA AI</h1>
        <span class="connection-status status-online">AI READY</span>
      </header>

      <div class="chat-messages dialog-box">
        <div class="chat-empty">
          <p>Welcome to Trackaa!</p>
          <p class="hint">Ask me anything about aircraft, flights, or maintenance.</p>
        </div>
      </div>

      <div class="chat-input-row">
        <input
          type="text"
          class="input chat-input"
          placeholder="Ask me anything..."
          autocomplete="off"
        />
        <button class="btn chat-send-btn">SEND</button>
      </div>
    </div>
  `;

  // Get references
  messagesContainer = container.querySelector('.chat-messages');
  inputElement = container.querySelector('.chat-input');
  sendButton = container.querySelector('.chat-send-btn');

  // Attach event listeners
  if (inputElement) {
    inputElement.addEventListener('keypress', handleInputKeypress);
    inputElement.addEventListener('input', handleInputChange);
    inputElement.addEventListener('focus', handleInputFocus);
    inputElement.addEventListener('blur', handleInputBlur);
    inputElement.focus();
  }

  if (sendButton) {
    sendButton.addEventListener('click', handleSendMessage);
  }

  // Subscribe to state changes
  if (unsubscribe) {
    unsubscribe();
  }
  unsubscribe = subscribe(handleStateChange);

  // Add welcome message (deferred to avoid triggering re-render during init)
  setTimeout(() => {
    addSystemMessage('Hello! I\'m your Trackaa AI assistant. Ask me about tracked aircraft, flights, or maintenance windows.');
  }, 0);
}

/**
 * Cleanup chat screen
 */
export function cleanupChatScreen(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }

  chatContainer = null;
  messagesContainer = null;
  inputElement = null;
  sendButton = null;
}
